import {
	IColumn,
	ILibraryQuestion as ICommonLibraryQuestion,
	IQuestionVariation as ICommonQuestionVariation,
	SourceType as CommonSourceType,
} from '@aon/tws-common-ui-questions/build/lib/models';
import _, { cloneDeep } from 'lodash';
import { AppDispatch } from '../appStore';
import ContentConstants from '../contentConstants';
import {
	AttributeName,
	IExternalAttributeVm,
	IQuestionBenchmarks,
	IQuestionLanguages,
	IQuestionTags,
	IQuestionTemplate,
	IQuestionTemplates,
	ITag,
} from '../models';
import * as normApi from '../services/BenchmarkLibraryAPI';
import * as questionApi from '../services/CommonQuestionAPI';
import * as customQuestionApi from '../services/CustomQuestionAPI';
import { LibraryQuestionColumnHelper, LibraryQuestionMapperUtility } from '../utilities';
import { convertSourceTypeFromQlToSb } from '../utilities/QuestionLibraryHelper';
import { IApplicationState } from './';
import { ActionTypes as CommonActionTypes } from './ActionTypes';
import { ActionTypes } from './CommonQuestionListStore';

export const ActionCreators = {
	GetQuestions: (isDemographic: boolean) => (dispatch: any, getState: () => IApplicationState) => {
		dispatch({ type: ActionTypes.QUESTIONS_GET_REQUESTED });

		const surveyId = getState().survey.surveyEditModel.survey.surveyId;
		const defaultCultureCode = getState().survey.surveyEditModel.defaultCultureCode;

		return Promise.all([
			questionApi.GetQuestions(isDemographic, defaultCultureCode),
			customQuestionApi.getCustomQuestions(surveyId, defaultCultureCode, isDemographic),
		])
			.then(([questions, customQuestions]) => {
				const unmappedLibraryQuestions = questions.items;
				const selectedQuestions = getState().survey.surveyEditModel.questions;
				// ICommonLibraryQuestion doesn't have defaultQuestionVariationId, yet debugging the object shows this property. Where does it come from?
				// Following mapApiToLibraryQuestsions lead and using any to access necessary properties.
				unmappedLibraryQuestions.forEach((uq: any) => {
					const selectedQuestion = selectedQuestions.find((sq) => sq.sourceQuestionVariationId == uq.defaultQuestionVariationId);
					if (selectedQuestion) {
						uq.parentQuestionId = selectedQuestion.parentQuestionId;
					}
				});
				const libraryQuestions = LibraryQuestionMapperUtility.mapApiToLibraryQuestions(unmappedLibraryQuestions);
				const customLibraryQuestions = LibraryQuestionMapperUtility.mapCustomQuestionsToLibraryQuestions(customQuestions);
				const allQuestions = libraryQuestions.concat(customLibraryQuestions);
				dispatch({
					type: ActionTypes.QUESTIONS_GET_SUCCEEDED,
					payload: {
						questions: allQuestions,
						columns: LibraryQuestionColumnHelper.getSurveyQuestionColumns(),
					},
				});
				dispatch(ActionCreators.GetQuestionLanguages());
				dispatch(ActionCreators.GetQuestionTags());
				dispatch(ActionCreators.GetQuestionTemplates());
				dispatch(ActionCreators.GetQuestionBenchmarks());
			})
			.catch((error: Error) => dispatch({ type: ActionTypes.QUESTIONS_GET_FAILED, payload: error.message }));
	},
	GetDemographicQuestions: () => (dispatch: any, getState: () => IApplicationState) => {
		dispatch({ type: ActionTypes.QUESTIONS_GET_REQUESTED });
		const defaultCultureCode = getState().survey.surveyEditModel.defaultCultureCode;
		return questionApi
			.GetQuestions(true, defaultCultureCode)
			.then((questions) => {
				const participantAttributes = getState().clientParticipantAttributes.participantAttributes;
				const libraryQuestions = LibraryQuestionMapperUtility.mapApiToLibraryQuestions(questions.items);
				const configuredAttributes = participantAttributes
					.filter((map: IExternalAttributeVm) => map.attributeName !== undefined)
					.map((map: IExternalAttributeVm) => map.attributeName as AttributeName);
				const mappedQuestions = libraryQuestions.filter(
					(q: ICommonLibraryQuestion) =>
						q.attributeName !== undefined && configuredAttributes.indexOf(q.attributeName as AttributeName) > -1,
				);

				dispatch({
					type: ActionTypes.QUESTIONS_GET_SUCCEEDED,
					payload: {
						questions: mappedQuestions,
						columns: LibraryQuestionColumnHelper.getDemographicQuestionColumns(),
					},
				});
				dispatch(ActionCreators.GetQuestionLanguages());
				dispatch(ActionCreators.GetQuestionTags());
			})
			.catch((error: Error) => dispatch({ type: ActionTypes.QUESTIONS_GET_FAILED, payload: error.message }));
	},
	GetQuestionLanguages: () => (dispatch: AppDispatch, getState: () => IApplicationState) => {
		return questionApi.GetQuestionLanguages().then((questionLanguages: IQuestionLanguages[]) => {
			const questions = cloneDeep(getState().commonQuestionList.questions);
			const languageMap = new Map<number, string[]>();
			questionLanguages.forEach((ql: IQuestionLanguages) => languageMap.set(ql.questionVariationId, ql.languageCodes));
			questions
				.filter((q: ICommonLibraryQuestion) => q.sourceType !== CommonSourceType.Custom)
				.forEach((q: ICommonLibraryQuestion) => {
					q.languages = languageMap.get(q.questionVariationId) || [];
					if (q.questionVariations && q.questionVariations.length > 0) {
						q.questionVariations.forEach((qv: ICommonQuestionVariation) => {
							const lang = questionLanguages.find((l: IQuestionLanguages) => l.questionVariationId === qv.questionVariationId);
							if (lang) {
								qv.languages = lang.languageCodes;
							}
						});
					}
				});

			dispatch({ type: ActionTypes.QUESTION_LANGUAGES_BG_GET_SUCCEEDED, payload: questions });
		});
	},
	GetQuestionBenchmarks: () => (dispatch: AppDispatch, getState: () => IApplicationState) => {
		return normApi.getBenchmarksForQuestions().then((questionBenchmarks: IQuestionBenchmarks[]) => {
			const questions: ICommonLibraryQuestion[] = _.cloneDeep(getState().commonQuestionList.questions);
			const benchmarkMap = new Map<number, number[]>();
			questionBenchmarks.forEach((qb: IQuestionBenchmarks) => benchmarkMap.set(qb.questionId, qb.benchmarkIds));
			questions.forEach((q: ICommonLibraryQuestion) => {
				q.benchmarkIds = benchmarkMap.get(q.questionId) || [];
			});
			dispatch({ type: ActionTypes.QUESTION_BENCHMARKS_BG_GET_SUCCEEDED, payload: questions });
		});
	},
	GetQuestionTags: () => (dispatch: AppDispatch, getState: () => IApplicationState) => {
		return questionApi.GetQuestionTags().then((tagData: IQuestionTags[]) => {
			const questions: ICommonLibraryQuestion[] = _.cloneDeep(getState().commonQuestionList.questions);
			const tagMap = new Map<number, string[]>();
			tagData.forEach((qt: IQuestionTags) => tagMap.set(qt.questionId, qt.tags && qt.tags.map((t: ITag) => t.name)));
			questions.forEach((q: ICommonLibraryQuestion) => (q.tagNames = tagMap.get(q.questionId)));
			dispatch({ type: ActionTypes.QUESTION_TAGS_BG_GET_SUCCEEDED, payload: questions });
		});
	},
	GetQuestionTemplates: () => (dispatch: AppDispatch, getState: () => IApplicationState) => {
		return questionApi.GetQuestionTemplates().then((templateData: IQuestionTemplates[]) => {
			const questions: ICommonLibraryQuestion[] = _.cloneDeep(getState().commonQuestionList.questions);
			const templateMap = new Map<number, string[]>();
			templateData.forEach((qt: IQuestionTemplates) =>
				templateMap.set(qt.questionId, qt.questionTemplates && qt.questionTemplates.map((t: IQuestionTemplate) => t.name)),
			);
			questions.forEach((q: ICommonLibraryQuestion) => (q.templateNames = templateMap.get(q.questionId)));
			dispatch({ type: ActionTypes.QUESTION_TEMPLATES_BG_GET_SUCCEEDED, payload: questions });
		});
	},
	ShowAllVariations: (showVariations: boolean) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SHOW_ALL_VARIATIONS, payload: showVariations });
	},
	ShowSingleVariation: (questionId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SHOW_SINGLE_VARIATION, payload: questionId });
	},
	UpdateColumns: (columns?: IColumn[]) => (dispatch: any) => {
		dispatch({ type: ActionTypes.UPDATE_COLUMNS, payload: columns });
	},
	SelectQuestion: (question: ICommonLibraryQuestion, isSelected: boolean, parentQuestionId?: number) => (dispatch: any) => {
		const sbQuestion: any = _.clone(question);
		sbQuestion.sourceType = convertSourceTypeFromQlToSb(question.sourceType);

		if (isSelected) {
			sbQuestion.parentQuestionId = parentQuestionId;
			if (question.scoredDimensionNames && question.scoredDimensionNames.length > 1) {
				dispatch(ActionCreators.SetSelectedQuestion(question));
				dispatch(ActionCreators.ToggleScoredDimensionModal(true));
			} else {
				dispatch({ type: CommonActionTypes.LIBRARY_QUESTION_SELECTED, payload: { question: sbQuestion, isSelected } });
				dispatch({ type: CommonActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_QUESTIONMODAL_ADDED });
				if (question.isDemographic) {
					dispatch({
						type: CommonActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_ADD_SELECTED_BYNAME,
						payload: question.attributeName,
					});
				}
			}
		} else {
			sbQuestion.parentQuestionId = undefined;
			dispatch({ type: CommonActionTypes.LIBRARY_QUESTION_SELECTED, payload: { question: sbQuestion, isSelected } });
			dispatch({ type: CommonActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_QUESTIONMODAL_REMOVED });
			if (question.isDemographic) {
				dispatch({
					type: CommonActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_REMOVE_SELECTED_BYNAME,
					payload: question.attributeName,
				});
			}
		}
	},
	// tslint:disable-next-line: max-line-length
	SelectQuestionVariation:
		(question: ICommonLibraryQuestion, questionVariation: ICommonQuestionVariation, isSelected: boolean) => (dispatch: any) => {
			const sbQuestion: any = _.clone(question);
			sbQuestion.sourceType = convertSourceTypeFromQlToSb(question.sourceType);
			if (isSelected) {
				if (question.scoredDimensionNames && question.scoredDimensionNames.length > 1) {
					dispatch(ActionCreators.SetSelectedQuestion(question, questionVariation.questionVariationId));
					dispatch(ActionCreators.ToggleScoredDimensionModal(true));
				} else {
					dispatch({
						type: CommonActionTypes.LIBRARY_QUESTIONVARIATION_SELECTED,
						payload: { question: sbQuestion, questionVariation, isSelected },
					});
					dispatch({
						type: CommonActionTypes.TOAST_INFO_NOTIFICATION,
						payload: ContentConstants.TOAST_QUESTIONMODAL_ADDED,
					});
					if (question.isDemographic) {
						dispatch({
							type: CommonActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_ADD_SELECTED_BYNAME,
							payload: question.attributeName,
						});
					}
				}
			} else {
				dispatch({
					type: CommonActionTypes.LIBRARY_QUESTIONVARIATION_SELECTED,
					payload: { question: sbQuestion, questionVariation, isSelected },
				});
				dispatch({
					type: CommonActionTypes.TOAST_INFO_NOTIFICATION,
					payload: ContentConstants.TOAST_QUESTIONMODAL_REMOVED,
				});
				if (question.isDemographic) {
					dispatch({
						type: CommonActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_REMOVE_SELECTED_BYNAME,
						payload: question.attributeName,
					});
				}
			}
		},
	ToggleScoredDimensionModal: (isOpen: boolean) => (dispatch: any) => {
		dispatch({
			type: ActionTypes.TOGGLE_SELECT_SCORED_DIMENSION_MODAL,
			payload: isOpen,
		});
	},
	SetSelectedQuestion: (question?: ICommonLibraryQuestion, questionVariationId?: number) => (dispatch: any) => {
		dispatch({
			type: ActionTypes.SET_SELECTED_QUESTION,
			payload: { question: question, questionVariationId: questionVariationId },
		});
	},
	SubmitScoredDimensionModal:
		(question: ICommonLibraryQuestion, selectedDimension: string) => (dispatch: any, getState: () => IApplicationState) => {
			let scoredDimensionNames: string[] = question.scoredDimensionNames || [];
			if (scoredDimensionNames.length > 0) {
				scoredDimensionNames = scoredDimensionNames.filter((sdn) => sdn !== selectedDimension);
			}
			let filteredDimensionNames: string[] = question.dimensionNames || [];
			filteredDimensionNames = filteredDimensionNames.filter(
				(fdn: string) => scoredDimensionNames.find((sdn) => sdn === fdn) === undefined,
			);
			const selectedQuestion = {
				...question,
				dimensionNames: filteredDimensionNames,
				scoredDimensionNames: [],
			};

			let questionVariation = undefined;
			if (getState().commonQuestionList.selectedQuestionVariationId && question.questionVariations) {
				questionVariation = question.questionVariations.find(
					(qv) => qv.questionVariationId === getState().commonQuestionList.selectedQuestionVariationId,
				);
			}

			if (questionVariation != undefined) {
				dispatch(ActionCreators.SelectQuestionVariation(selectedQuestion, questionVariation, true));
			} else {
				dispatch(ActionCreators.SelectQuestion(selectedQuestion, true));
			}

			dispatch(ActionCreators.ToggleScoredDimensionModal(false));
			dispatch(ActionCreators.SetSelectedQuestion());
		},
	CancelSelectScoreDimensionModal: () => (dispatch: any) => {
		dispatch(ActionCreators.ToggleScoredDimensionModal(false));
		dispatch(ActionCreators.SetSelectedQuestion());
	},
};
